import { get, set, merge } from 'lodash';
import moment from 'moment';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems  } from 'resolvers/getVisibleEntities';
import { resolveEntityCellData } from  'resolvers/resolveEntityCellData';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import { getStore } from 'store';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc ToDoItemDetailSummaryWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { ToDoItemDetailSummaryWidgetService } from 'services/toDoItemDetailSummaryWidget';
 *
 * // create service instance and send/cancel request
 * const toDoItemDetailSummaryWidgetService = new ToDoItemDetailSummaryWidgetService();
 * const result = await toDoItemDetailSummaryWidgetService.fetchData( '<user-id>' ); // fetch data
 * toDoItemDetailSummaryWidgetService.cancel(); // cancel an active request
 */
export class ToDoItemDetailSummaryWidgetService extends HttpService {

    // make request and fetch data
    async fetchComponentData(  { url = {}, data = {} } = {}  ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            const component = get( response, 'body.component', {} );
            const visibility = get( component, 'visibility', {} );
            const primarySection = get( component, 'structure.primaryFields', {} );
            const secondarySection = get( component, 'structure.secondaryFields', {} );
            const actions = getVisibleItems( get( component, 'structure.actions', [] ), visibility );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                data: {
                    toDoData: get( response, 'body.component.data.state', {} ),
                    primarySection: resolveEntityCellData( { items: getVisibleItems( primarySection, visibility ), component } ),
                    secondarySection: resolveEntityCellData( { items: getVisibleItems( secondarySection, visibility ), component } ),
                    visibility,
                    actions
                }
            };
            
        } catch( errResponse ) {
            return this._errorFormatter( errResponse );
        }
    }

    // fetch case data
    async viewCaseData( { url, data  } ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        const response = await this.post( {
            host: CONFIG.host,
            path: url,
            data: merge( data, {
                context: {
                    callId,
                }
            } ),
        } );

        return {
            status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
            data: {
                data: get( response, 'body.component.data', [] ),
                ...get( response, 'body.component.structure', [] )
            }
        };
    }

    // initiate an outbound call (for member)
    async initiateOutboundCall( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = MEMBER_SEARCH_MODULE.DASHBOARD;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        module,                    },
                } ),
            } );

            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

                // set the callStartTime to local UTC time
                set( response, 'body.component.data.state.callStartTime', moment.utc().format() );
                    
                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    data: {
                        callData: get( response, 'body.component.data.state', {} )
                    },
                };
            } else {

                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                    data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
                };
            }

            
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

}
